import type { NoveltyCalcAffectedProduct, NoveltyCalcErrorGroup } from '@/api';
import { formatPriceField } from '@/components/table/table-format-helpers';

const DETAIL_LABELS: Record<string, string> = {
  initialRrp: 'Initial RRP',
  localLeadRrp: 'Local Lead RRP',
  themeId: 'Theme',
  groupId: 'Group',
  isIpLicensed: 'IP Licensed',
};

const PRICE_DETAIL_KEYS = new Set(['initialRrp', 'localLeadRrp']);

const formatDetailKey = (key: string): string =>
  Object.hasOwn(DETAIL_LABELS, key) ? (DETAIL_LABELS[key] ?? key) : key;

const formatDetailValue = (key: string, value: string): string => {
  const price = Number(value);
  return PRICE_DETAIL_KEYS.has(key) &&
    value.trim() !== '' &&
    Number.isFinite(price)
    ? formatPriceField(price, true)
    : value;
};

export const formatDetail = (
  detail: Record<string, string | null> | null | undefined,
): string | null => {
  if (!detail) return null;
  return (
    Object.entries(detail)
      .filter((entry): entry is [string, string] => entry[1] != null)
      .map(
        ([key, value]) =>
          `${formatDetailKey(key)}: ${formatDetailValue(key, value)}`,
      )
      .join(', ') || null
  );
};

export const formatAffectedProduct = (
  product: NoveltyCalcAffectedProduct,
): string => {
  const parts: string[] = [];
  if (product.productId != null) parts.push(`Product ID ${product.productId}`);
  if (product.priceUnitId != null)
    parts.push(`Price List ${product.priceUnitId}`);
  return parts.join(', ');
};

const isRenderable = (product: NoveltyCalcAffectedProduct): boolean =>
  product.productId != null || product.priceUnitId != null;

export const renderableProducts = (
  group: NoveltyCalcErrorGroup,
): NoveltyCalcAffectedProduct[] => group.affectedProducts.filter(isRenderable);

export const groupErrorCount = (group: NoveltyCalcErrorGroup): number =>
  renderableProducts(group).length || 1;

const totalErrorCount = (errors: NoveltyCalcErrorGroup[]): number =>
  errors.reduce((sum, group) => sum + groupErrorCount(group), 0);

const hasProductIds = (group: NoveltyCalcErrorGroup): boolean =>
  group.affectedProducts.some((product) => product.productId != null);

const pluralizeProducts = (count: number): string =>
  `${count} ${count === 1 ? 'product' : 'products'} skipped`;

export const formatErrorSummary = (errors: NoveltyCalcErrorGroup[]): string => {
  const [group] = errors;
  if (!group) return '';

  if (errors.length > 1) {
    return `Multiple errors detected - ${pluralizeProducts(totalErrorCount(errors))}`;
  }

  return hasProductIds(group)
    ? `Error: ${pluralizeProducts(groupErrorCount(group))}`
    : 'Calculation error';
};
